import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, switchMap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { ApiError } from '../models/api.model';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const notification = inject(NotificationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let apiError: ApiError = {
        status: error.status,
        error: error.error?.error || 'Unknown Error',
        message: error.error?.message || 'Something went wrong',
        timestamp: new Date().toISOString()
      };

      if (error.status === 401) {
        // Avoid infinite loop if refresh token itself is invalid or we are on login page
        if (req.url.includes('/auth/login') || req.url.includes('/auth/refresh')) {
          if (req.url.includes('/auth/refresh')) {
            console.error('[ErrorInterceptor] Refresh token invalid. Logging out.');
            authService.logout();
          }
          return throwError(() => apiError);
        }

        console.warn(`[ErrorInterceptor] 401 Unauthorized for ${req.url}. Attempting refresh...`);
        return authService.refreshToken().pipe(
          switchMap(() => {
            console.log(`[ErrorInterceptor] Refresh successful. Retrying ${req.url}`);
            return next(req);
          }),
          catchError((refreshError) => {
            console.error(`[ErrorInterceptor] Refresh failed. Logging out.`);
            authService.logout();
            return throwError(() => refreshError);
          })
        );
      }

      if (error.status === 403) {
        notification.error('Access Denied: You do not have permission.');
      } else if (error.status === 500) {
        notification.error('Server Error: Please try again later.');
      } else if (error.status === 0) {
        notification.error('Network Error: Cannot connect to server.');
      } else {
        notification.error(apiError.message);
      }

      return throwError(() => apiError);
    })
  );
};
